import React, { useState, useEffect, useRef } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import axios from "axios";
import Snackbar from "@mui/material/Snackbar";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";

const VOSFACTURES_BASE_URL = {
  otedalon: "https://otedalons.vosfactures.fr",
  zotdevis: "https://zotdevis.vosfactures.fr",
};

const apiUrl = process.env.REACT_APP_API_URL;

const Toggle = ({ checked, onChange }) => (
  <div
    onClick={() => onChange(!checked)}
    role="checkbox"
    aria-checked={checked}
    style={{
      width: "34px",
      height: "19px",
      borderRadius: "10px",
      background: checked ? "#2563eb" : "#d1d5db",
      position: "relative",
      cursor: "pointer",
      flexShrink: 0,
      transition: "background 0.15s ease",
    }}
  >
    <div
      style={{
        width: "15px",
        height: "15px",
        borderRadius: "50%",
        background: "white",
        position: "absolute",
        top: "2px",
        left: checked ? "17px" : "2px",
        transition: "left 0.15s ease",
        boxShadow: "0 1px 2px rgba(0,0,0,0.25)",
      }}
    />
  </div>
);

// Bouton "à menu" maison (remplace le SplitButton de PrimeReact).
// Autonome : chaque instance gère son propre état ouvert/fermé et sa
// fermeture au clic extérieur / touche Echap, sans dépendre d'un état
// global ni du bouton lui-même pour se fermer.
const SplitMenuButton = ({
  items,
  disabled = false,
  variant = "button", // "button" (pleine largeur, libellé + icône) | "icon" (rond, icône seule)
  label,
  loadingLabel,
  loading = false,
  icon,
  loadingIcon = "pi pi-spin pi-spinner",
  iconColor = "#374151",
  menuAlign = "left", // "left" | "right"
  className,
}) => {
  const [open, setOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState(null);
  const wrapperRef = useRef(null);
  const triggerRef = useRef(null);

  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  const toggleMenu = () => {
    if (disabled) return;
    if (!open && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setMenuStyle({
        top: rect.bottom + window.scrollY + 4,
        left: menuAlign === "left" ? rect.left + window.scrollX : undefined,
        right:
          menuAlign === "right"
            ? window.innerWidth - rect.right - window.scrollX
            : undefined,
        minWidth: variant === "button" ? rect.width : 140,
      });
    }
    setOpen((prev) => !prev);
  };

  const handleItemClick = (item) => {
    setOpen(false);
    item.command();
  };

  const isIcon = variant === "icon";

  return (
    <div
      ref={wrapperRef}
      className={className}
      style={{ display: isIcon ? "inline-flex" : "flex", width: isIcon ? "auto" : "100%" }}
    >
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        onClick={toggleMenu}
        style={
          isIcon
            ? {
                width: "28px",
                height: "28px",
                borderRadius: "50%",
                border: "none",
                background: "transparent",
                color: iconColor,
                fontSize: "12px",
                cursor: disabled ? "not-allowed" : "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 0,
                opacity: disabled ? 0.4 : 1,
              }
            : {
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
                width: "100%",
                background: "#dbeafe",
                border: "1px solid #bfdbfe",
                color: "#1d4ed8",
                fontSize: "14px",
                fontWeight: 500,
                borderRadius: "6px",
                padding: "8px 16px",
                cursor: disabled ? "not-allowed" : "pointer",
                opacity: disabled ? 0.6 : 1,
              }
        }
      >
        <i className={loading ? loadingIcon : icon} />
        {!isIcon && <span>{loading ? loadingLabel : label}</span>}
        {!isIcon && <i className="pi pi-chevron-down" style={{ fontSize: "10px" }} />}
      </button>

      {open && !disabled && menuStyle && (
        <div
          style={{
            position: "fixed",
            top: menuStyle.top,
            left: menuStyle.left,
            right: menuStyle.right,
            minWidth: menuStyle.minWidth,
            background: "white",
            border: "1px solid #e5e7eb",
            borderRadius: "6px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            padding: "4px 0",
            zIndex: 1000,
          }}
        >
          {items.map((item, index) => (
            <div
              key={index}
              onClick={() => handleItemClick(item)}
              style={{
                padding: "8px 16px",
                cursor: "pointer",
                fontSize: "13px",
                color: "#374151",
                whiteSpace: "nowrap",
                transition: "background 0.15s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "#f3f4f6";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "transparent";
              }}
            >
              {item.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default function DevisDemenagementDialog({
  visible,
  onHide,
  ficheData,
  onDevisGenerated,
  forceNew,
}) {
  // États pour la logistique
  const [nbDemenageurs, setNbDemenageurs] = useState(2);
  const [nbCamions, setNbCamions] = useState(1);
  const [prixLogistique, setPrixLogistique] = useState(0);

  // Cartons (pas de prix dynamique, tout est inclus dans prixLogistique)
  const [nbCartons, setNbCartons] = useState(0);

  // Mise en carton (pas de prix dynamique)
  const [miseEnCarton, setMiseEnCarton] = useState(false);

  // Adresses spécifiques au déménagement (départ / arrivée)
  const [adresseDepart, setAdresseDepart] = useState("");
  const [adresseArrivee, setAdresseArrivee] = useState("");

  // Démontage / remontage (pas de prix dynamique)
  const [demontage, setDemontage] = useState(false);
  const [meublesDemontageList, setMeublesDemontageList] = useState([]);

  // Mode d'envoi du devis
  const [modeEnvoi, setModeEnvoi] = useState(""); // "otedalon" | "zotdevis"

  const [remise, setRemise] = useState(0);
  const [options, setOptions] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [devisInfo, setDevisInfo] = useState(null);
  const [loading, setLoading] = useState(false);

  // Liste de tous les devis existants pour ce client
  const [devisList, setDevisList] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const getResendItems = (row) => [
    {
      label: "Otedalon",
      command: () => handleRenvoyerDevis(row, "otedalon"),
    },
    {
      label: "Zotdevis",
      command: () => handleRenvoyerDevis(row, "zotdevis"),
    },
  ];

  const [isForceNew, setIsForceNew] = useState(forceNew);

  const generateMenuItems = [
    {
      label: "Otedalon",
      command: () => handleGenererDevis("otedalon"),
    },
    {
      label: "Zotdevis",
      command: () => handleGenererDevis("zotdevis"),
    },
  ];

  useEffect(() => {
    setIsForceNew(forceNew);
  }, [forceNew]);

  // États pour l'édition des infos client
  const [hasChanges, setHasChanges] = useState(false);
  const [tempNom, setTempNom] = useState("");
  const [tempEmail, setTempEmail] = useState("");
  const [tempTel, setTempTel] = useState("");
  const [tempRegion, setTempRegion] = useState("");
  const [tempAdresse, setTempAdresse] = useState("");
  const [tempNote, setTempNote] = useState("");



  const [selectedGenerateMode, setSelectedGenerateMode] = useState("otedalon");


  // Initialiser les données du client
  useEffect(() => {
    if (ficheData) {
      setTempNom(ficheData.nom || "");
      setTempEmail(ficheData.email || "");
      setTempTel(ficheData.tel || "");
      setTempRegion(ficheData.region || "");
      setTempAdresse(ficheData.adresse || "");
      setTempNote(ficheData.note || "");
      setHasChanges(false);
    }
  }, [ficheData]);

  // Vérifier les changements
  useEffect(() => {
    if (ficheData) {
      const hasAnyChange =
        tempNom !== (ficheData.nom || "") ||
        tempEmail !== (ficheData.email || "") ||
        tempTel !== (ficheData.tel || "") ||
        tempRegion !== (ficheData.region || "") ||
        tempAdresse !== (ficheData.adresse || "");

      setHasChanges(hasAnyChange);
    }
  }, [tempNom, tempEmail, tempTel, tempRegion, tempAdresse, ficheData]);

  useEffect(() => {
    if (visible && ficheData?.idFicheDem) {
      if (isForceNew) {
        setDevisInfo(null);
        setLoading(false);
        setNbDemenageurs(2);
        setNbCamions(1);
        setPrixLogistique(0);
        setNbCartons(0);
        setMiseEnCarton(false);
        setAdresseDepart("");
        setAdresseArrivee("");
        setDemontage(false);
        setMeublesDemontageList([]);
        setOptions([]);
        setRemise(0);
        setModeEnvoi("");
      } else if (!devisInfo) {
        checkExistingDevis();
      }
    }
  }, [visible, ficheData, isForceNew]);

  const checkExistingDevis = async () => {
    // if (devisInfo?.a_devis === 1) {
    //   setLoading(false);
    //   return;
    // }

    setLoading(true);

    try {
      const response = await axios.get(
        `${apiUrl}/IHM/api/devis_demenagement.php?fiche_id=${ficheData.idFicheDem}`,
      );
      if (response.data.a_devis == 1) {
        setDevisInfo({
          a_devis: response.data.a_devis,
          numero_devis:
            response.data.dernier_devis_id || response.data.numero_devis,
          lien_devis: response.data.lien_devis,
          lien_pdf: response.data.lien_pdf,
          token_devis: response.data.token_devis,
          montant_devis: response.data.montant_total || 0,
          date_devis:
            response.data.date_devis ||
            response.data.date_creation ||
            new Date().toISOString(),
          idVosFactures: response.data.idVosFactures,
          statut_devis: response.data.statut_devis || "brouillon",
          nb_demenageurs: response.data.nb_demenageurs,
          nb_camions: response.data.nb_camions,
          nb_cartons: response.data.nb_cartons,
          mise_en_carton: response.data.mise_en_carton == 1,
          demontage: response.data.demontage == 1,
          meubles_demontage: response.data.meubles_demontage,
          adresse_depart: response.data.adresse_depart,
          adresse_arrivee: response.data.adresse_arrivee,
        });
        fetchDevisList();
      } else {
        setDevisInfo(null);
      }
    } catch (error) {
      console.error("Erreur lors de la vérification du devis:", error);
    } finally {
      setLoading(false);
    }
  };

  // Récupère la liste de tous les devis existants pour ce client
  const fetchDevisList = async () => {
    if (!ficheData?.idFicheDem) return;
    setLoadingList(true);
    try {
      const response = await axios.get(
        `${apiUrl}/IHM/api/devis_demenagement.php?fiche_id=${ficheData.idFicheDem}&list=1`,
      );
      setDevisList(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error(
        "Erreur lors de la récupération de la liste des devis:",
        error,
      );
    } finally {
      setLoadingList(false);
    }
  };

  // Renvoyer un devis (n'importe quelle version) par mail via le compte choisi
  const handleRenvoyerDevis = async (devis, mode) => {
    try {
      const res = await axios.post(`${apiUrl}/IHM/api/sendMailDevisDem.php`, {
        id: ficheData.idFicheDem,
        id_devis: devis.id_devis,
        mode_envoi: mode,
        adminMod: localStorage.getItem("us"),
      });
      if (res.data.success) {
        setSnackbarMessage(
          `Devis ${devis.numero_devis} renvoyé à ${ficheData.email}`,
        );
        setOpenSnackbar(true);
        fetchDevisList();
      } else {
        setSnackbarMessage(res.data.message || "Envoi impossible");
        setOpenSnackbar(true);
      }
    } catch (err) {
      console.error("Erreur envoi mail:", err);
      setSnackbarMessage("Impossible d'envoyer le mail");
      setOpenSnackbar(true);
    }
  };

  const handleCloseSnackbar = () => setOpenSnackbar(false);

  // Calculs du devis
  // Cartons / mise en carton / démontage : pas de prix, tout est inclus dans prixLogistique
  const sousTotalPrestation = prixLogistique;

  const totalOptions = options.reduce(
    (sum, opt) => sum + (parseFloat(opt.prix) || 0),
    0,
  );

  const sousTotal = sousTotalPrestation + totalOptions;
  const montantRemise = (sousTotal * remise) / 100;
  const total = sousTotal - montantRemise;

  // Options libres
  const ajouterOption = () => {
    setOptions([...options, { titre: "", description: "", prix: 0 }]);
  };

  const supprimerOption = (index) => {
    const newOptions = [...options];
    newOptions.splice(index, 1);
    setOptions(newOptions);
  };

  const modifierOption = (index, champ, valeur) => {
    const newOptions = [...options];
    newOptions[index][champ] = valeur;
    setOptions(newOptions);
  };

  // Meubles à démonter / remonter
  const ajouterMeuble = () => {
    setMeublesDemontageList([...meublesDemontageList, ""]);
  };

  const supprimerMeuble = (index) => {
    const newList = [...meublesDemontageList];
    newList.splice(index, 1);
    setMeublesDemontageList(newList);
  };

  const modifierMeuble = (index, valeur) => {
    const newList = [...meublesDemontageList];
    newList[index] = valeur;
    setMeublesDemontageList(newList);
  };

  // Générer le devis
  const handleGenererDevis = async (mode) => {
    if (!ficheData) return;
    setGenerating(true);
    try {
      const response = await axios.post(
        `${apiUrl}/IHM/api/devis_demenagement.php`,
        {
          fiche_id: ficheData.idFicheDem,
          nom: ficheData.nom,
          email: ficheData.email,
          telephone: ficheData.tel,
          region: ficheData.region,
          adresse: ficheData.adresse,
          adresse_depart: adresseDepart,
          adresse_arrivee: adresseArrivee,
          nb_demenageurs: nbDemenageurs,
          nb_camions: nbCamions,
          prix_logistique: prixLogistique,
          carton: nbCartons > 0,
          nb_cartons: nbCartons,
          mise_en_carton: miseEnCarton,
          demontage: demontage,
          meubles_demontage: meublesDemontageList
            .filter((m) => m.trim() !== "")
            .join(", "),
          remise: remise,
          options: options,
          is_new_version: isForceNew,
          mode_envoi: mode,
          adminMod: localStorage.getItem("us"),
        },
      );

      if (response.data.success) {
        const newDevisInfo = {
          a_devis: 1,
          lien_devis: response.data.devis_url,
          lien_pdf: response.data.pdf_url,
          numero_devis: response.data.devis_number,
          montant_devis: response.data.montant,
          idVosFactures: response.data.idVosFactures || null,
          statut_devis: "brouillon",
          date_devis: new Date().toISOString(),
        };

        setDevisInfo(newDevisInfo);
        setIsForceNew(false);
        fetchDevisList();

        setSnackbarMessage("Devis généré avec succès !");
        setOpenSnackbar(true);
        if (onDevisGenerated) onDevisGenerated();
      } else {
        setSnackbarMessage(
          response.data.message || "Erreur lors de la génération",
        );
        setOpenSnackbar(true);
      }
    } catch (error) {
      console.error("Erreur:", error);
      setSnackbarMessage("Erreur lors de la génération du devis");
      setOpenSnackbar(true);
    } finally {
      setGenerating(false);
    }
  };

  // Sauvegarder les modifications client
  const handleSaveClient = async () => {
    try {
      await axios.post(`${apiUrl}/IHM/api/update_ficheDem.php`, {
        idFicheDem: ficheData.idFicheDem,
        nom: tempNom,
        telephone: tempTel,
        email: tempEmail,
        region: tempRegion,
        adresse: tempAdresse,
        note: tempNote,
        adminMod: localStorage.getItem("us"),
      });

      setHasChanges(false);
      setSnackbarMessage("Informations client mises à jour");
      setOpenSnackbar(true);

      if (onDevisGenerated) onDevisGenerated();
    } catch (error) {
      console.error("Erreur de mise à jour :", error);
      setSnackbarMessage("Erreur lors de la sauvegarde");
      setOpenSnackbar(true);
    }
  };

  // Header du dialog
  const header = (
    <div className="flex justify-content-between align-items-center w-full">
      <span className="font-semibold text-lg">
        Fiche client & devis déménagement
      </span>
    </div>
  );

  return (
    <>
      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        open={openSnackbar}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        message={snackbarMessage}
        action={
          <IconButton
            size="small"
            color="inherit"
            onClick={handleCloseSnackbar}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        }
      />

      <Dialog
        blockScroll
        visible={visible}
        header={header}
        className="custom-dialog"
        style={{ width: "50vw", maxWidth: "770px" }}
        modal
        onHide={() => {
          onHide();
          setOptions([]);
          // setIsForceNew(false);
          setDevisInfo(null);
          // setLoading(false);
          // setNbDemenageurs(2);
          // setNbCamions(1);
          // setPrixLogistique(0);
          // setCarton(false);
          // setNbCartons(0);
          // setMiseEnCarton(false);
          // setDemontage(false);
          // setMeublesDemontage("");
          // setRemise(0);
          // setModeEnvoi("");
          setDevisList([]);



        }}
        breakpoints={{ "960px": "90vw", "640px": "95vw" }}
      >
        <div className="p-3">
          {/* Section 1 - Informations client */}
          <div className="grid">
            <div className="col-12">
              <div
                style={{
                  background: "#f3f4f6",
                  padding: "4px 8px",
                  borderRadius: "4px",
                  marginBottom: "10px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <div
                  style={{ display: "flex", alignItems: "center", gap: "6px" }}
                >
                  <span
                    style={{
                      background: "#374151",
                      color: "white",
                      borderRadius: "50%",
                      width: "18px",
                      height: "18px",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "11px",
                      fontWeight: 700,
                    }}
                  >
                    1
                  </span>
                  <span
                    style={{
                      fontSize: "11px",
                      fontWeight: 600,
                      color: "#374151",
                      letterSpacing: "0.05em",
                    }}
                  >
                    INFORMATIONS CLIENT
                  </span>
                </div>

                {hasChanges && (
                  <span
                    onClick={() => handleSaveClient()}
                    style={{
                      border: "1px solid #22c55e",
                      borderRadius: "50%",
                      color: "#fff",
                      width: "28px",
                      height: "28px",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      backgroundColor: "#22c55e",
                    }}
                  >
                    <SaveIcon sx={{ fontSize: 16 }} />
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Infos client */}
          <div className="grid">
            <div className="col-12 md:col-4">
              <label
                style={{
                  fontSize: "12px",
                  color: "#374151",
                  display: "block",
                  marginBottom: "2px",
                }}
              >
                Nom
              </label>
              <input
                value={tempNom}
                onChange={(e) => setTempNom(e.target.value)}
                style={{
                  width: "100%",
                  height: "36px",
                  padding: "0 8px",
                  border: "1px solid #d1d5db",
                  borderRadius: "5px",
                  background: "white",
                  fontSize: "14px",
                }}
              />
            </div>
            <div className="col-12 md:col-4">
              <label
                style={{
                  fontSize: "12px",
                  color: "#374151",
                  display: "block",
                  marginBottom: "2px",
                }}
              >
                Email
              </label>
              <input
                value={tempEmail}
                onChange={(e) => setTempEmail(e.target.value)}
                style={{
                  width: "100%",
                  height: "36px",
                  padding: "0 8px",
                  border: "1px solid #d1d5db",
                  borderRadius: "5px",
                  background: "white",
                  fontSize: "14px",
                }}
              />
            </div>
            <div className="col-12 md:col-4">
              <label
                style={{
                  fontSize: "12px",
                  color: "#374151",
                  display: "block",
                  marginBottom: "2px",
                }}
              >
                Téléphone
              </label>
              <input
                value={tempTel}
                onChange={(e) => setTempTel(e.target.value)}
                style={{
                  width: "100%",
                  height: "36px",
                  padding: "0 8px",
                  border: "1px solid #d1d5db",
                  borderRadius: "5px",
                  background: "white",
                  fontSize: "14px",
                }}
              />
            </div>
          </div>

          <div className="grid">
            <div className="col-12 md:col-6">
              <label
                style={{
                  fontSize: "12px",
                  color: "#374151",
                  display: "block",
                  marginBottom: "2px",
                }}
              >
                Région
              </label>
              <input
                value={tempRegion}
                onChange={(e) => setTempRegion(e.target.value)}
                style={{
                  width: "100%",
                  height: "36px",
                  padding: "0 8px",
                  border: "1px solid #d1d5db",
                  borderRadius: "5px",
                  fontSize: "14px",
                }}
              />
            </div>
            <div className="col-12 md:col-6">
              <label
                style={{
                  fontSize: "12px",
                  color: "#374151",
                  display: "block",
                  marginBottom: "2px",
                }}
              >
                Adresse
              </label>
              <input
                value={tempAdresse}
                onChange={(e) => setTempAdresse(e.target.value)}
                style={{
                  width: "100%",
                  height: "36px",
                  padding: "0 8px",
                  border: "1px solid #d1d5db",
                  borderRadius: "5px",
                  fontSize: "14px",
                }}
              />
            </div>
          </div>

          {/* Section 2 - Génération du devis */}
          {loading ? (
            <div className="flex justify-content-center p-5">
              <i
                className="pi pi-spin pi-spinner"
                style={{ fontSize: "2rem", color: "#2563eb" }}
              ></i>
            </div>
          ) : !devisInfo?.a_devis || isForceNew ? (
            <>
              <div className="grid">
                <div className="col-12">
                  <div
                    style={{
                      background: "#f3f4f6",
                      padding: "4px 8px",
                      borderRadius: "4px",
                      marginBottom: "10px",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                  >
                    <span
                      style={{
                        background: "#374151",
                        color: "white",
                        borderRadius: "50%",
                        width: "18px",
                        height: "18px",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "11px",
                        fontWeight: 700,
                      }}
                    >
                      2
                    </span>
                    <span
                      style={{
                        fontSize: "11px",
                        fontWeight: 600,
                        color: "#374151",
                        letterSpacing: "0.05em",
                      }}
                    >
                      LOGISTIQUE
                    </span>
                  </div>
                </div>
              </div>

              {/* Adresses de départ et d'arrivée */}
              <div className="grid">
                <div className="col-12 md:col-6">
                  <label
                    style={{
                      fontSize: "11px",
                      color: "#374151",
                      display: "block",
                      marginBottom: "2px",
                    }}
                  >
                    Adresse de départ
                  </label>
                  <input
                    value={adresseDepart}
                    onChange={(e) => setAdresseDepart(e.target.value)}
                    placeholder="Ex: 2 rue du temple, résidence diamant, Terre Sainte"
                    style={{
                      width: "100%",
                      height: "35px",
                      padding: "0 8px",
                      border: "1px solid #d1d5db",
                      borderRadius: "5px",
                      fontSize: "14px",
                      boxSizing: "border-box",
                    }}
                  />
                </div>
                <div className="col-12 md:col-6">
                  <label
                    style={{
                      fontSize: "11px",
                      color: "#374151",
                      display: "block",
                      marginBottom: "2px",
                    }}
                  >
                    Adresse d'arrivée
                  </label>
                  <input
                    value={adresseArrivee}
                    onChange={(e) => setAdresseArrivee(e.target.value)}
                    placeholder="Ex: 35 Rue Augustin Archambaud, Saint-Pierre"
                    style={{
                      width: "100%",
                      height: "35px",
                      padding: "0 8px",
                      border: "1px solid #d1d5db",
                      borderRadius: "5px",
                      fontSize: "14px",
                      boxSizing: "border-box",
                    }}
                  />
                </div>
              </div>

              {/* Logistique */}
              <div className="grid">
                <div className="col-12">
                  <div
                    style={{
                      display: "flex",
                      gap: "8px",
                      alignItems: "flex-end",
                      flexWrap: "wrap",
                    }}
                  >
                    <div style={{ flex: "1", minWidth: "80px" }}>
                      <label
                        style={{
                          fontSize: "11px",
                          color: "#374151",
                          display: "block",
                          marginBottom: "2px",
                        }}
                      >
                        Déménageurs
                      </label>
                      <input
                        type="number"
                        value={nbDemenageurs}
                        onChange={(e) =>
                          setNbDemenageurs(parseInt(e.target.value) || 0)
                        }
                        min={0}
                        style={{
                          width: "80px",
                          height: "35px",
                          padding: "0 6px",
                          border: "1px solid #d1d5db",
                          borderRadius: "5px",
                          fontSize: "14px",
                        }}
                      />
                    </div>

                    <div style={{ flex: "1", minWidth: "80px" }}>
                      <label
                        style={{
                          fontSize: "11px",
                          color: "#374151",
                          display: "block",
                          marginBottom: "2px",
                        }}
                      >
                        Camions
                      </label>
                      <input
                        type="number"
                        value={nbCamions}
                        onChange={(e) =>
                          setNbCamions(parseInt(e.target.value) || 0)
                        }
                        min={0}
                        style={{
                          width: "80px",
                          height: "35px",
                          padding: "0 6px",
                          border: "1px solid #d1d5db",
                          borderRadius: "5px",
                          fontSize: "14px",
                        }}
                      />
                    </div>

                    <div style={{ flex: "1", minWidth: "80px" }}>
                      <label
                        style={{
                          fontSize: "11px",
                          color: "#374151",
                          display: "block",
                          marginBottom: "2px",
                        }}
                      >
                        Prix logistique (€)
                      </label>
                      <input
                        type="number"
                        value={prixLogistique}
                        onChange={(e) =>
                          setPrixLogistique(parseFloat(e.target.value) || 0)
                        }
                        min={0}
                        step={1}
                        style={{
                          width: "110px",
                          height: "35px",
                          padding: "0 6px",
                          border: "1px solid #d1d5db",
                          borderRadius: "5px",
                          fontSize: "14px",
                        }}
                      />
                    </div>

                    <div style={{ flex: "1", minWidth: "80px" }}>
                      <label
                        style={{
                          fontSize: "11px",
                          color: "#374151",
                          display: "block",
                          marginBottom: "2px",
                        }}
                      >
                        Remise %
                      </label>
                      <input
                        type="number"
                        value={remise}
                        onChange={(e) =>
                          setRemise(parseFloat(e.target.value) || 0)
                        }
                        min={0}
                        max={100}
                        style={{
                          width: "70px",
                          height: "35px",
                          padding: "0 6px",
                          border: "1px solid #d1d5db",
                          borderRadius: "5px",
                          fontSize: "14px",
                        }}
                      />
                    </div>

                    <div style={{ flex: "1", minWidth: "80px" }}>
                      <label
                        style={{
                          fontSize: "11px",
                          color: "#374151",
                          display: "block",
                          marginBottom: "2px",
                        }}
                      >
                        Nb cartons
                      </label>
                      <input
                        type="number"
                        value={nbCartons}
                        onChange={(e) =>
                          setNbCartons(parseInt(e.target.value) || 0)
                        }
                        min={0}
                        style={{
                          width: "80px",
                          height: "35px",
                          padding: "0 6px",
                          border: "1px solid #d1d5db",
                          borderRadius: "5px",
                          fontSize: "14px",
                        }}
                      />
                    </div>



                     <div style={{ flex: "0 0 auto" }}>
                      <label
                        style={{
                          fontSize: "11px",
                          color: "#374151",
                          display: "block",
                          marginBottom: "2px",
                        }}
                      >
                        Options
                      </label>
                      <button
                        onClick={ajouterOption}
                        style={{
                          width: "35px",
                          height: "35px",
                          border: "1px solid #d1d5db",
                          borderRadius: "5px",
                          background: "white",
                          fontSize: "20px",
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        +
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Mise en carton / Démontage */}
              <div className="flex mt-2">
                <div className="flex items-center gap-4 border border-gray-200 rounded-md py-2 flex-nowrap">
                  {/* Mise en carton */}
                  <div className="flex items-center gap-2 shrink-0">
                    {" "}
                    <div
                      style={{
                        height: "32px",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <Toggle
                        checked={miseEnCarton}
                        onChange={setMiseEnCarton}
                      />
                    </div>
                    <span
                      className="text-sm font-medium whitespace-nowrap"
                      style={{ lineHeight: "32px" }}
                    >
                      Mise en cartons
                    </span>
                  </div>

                  {/* Démontage */}
                  <div className="flex items-center gap-2 shrink-0">
                    {" "}
                    {/* 👈 Ajouté shrink-0 */}
                    <div
                      style={{
                        height: "32px",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <Toggle checked={demontage} onChange={setDemontage} />
                    </div>
                    <span
                      className="text-sm font-medium whitespace-nowrap"
                      style={{ lineHeight: "32px" }}
                    >
                      Démontage
                    </span>
                  </div>
                </div>
              </div>

              {demontage && (
                <div className="grid">
                  <div className="col-12">
                    <label
                      style={{
                        fontSize: "11px",
                        color: "#374151",
                        display: "block",
                        marginBottom: "4px",
                      }}
                    >
                      Meubles à démonter / remonter
                    </label>

                    {meublesDemontageList.map((meuble, index) => (
                      <div
                        key={index}
                        style={{
                          display: "flex",
                          gap: "8px",
                          alignItems: "center",
                          marginBottom: "6px",
                        }}
                      >
                        <input
                          value={meuble}
                          onChange={(e) =>
                            modifierMeuble(index, e.target.value)
                          }
                          placeholder="Ex: Armoire, lit, table..."
                          style={{
                            flex: 1,
                            height: "35px",
                            padding: "0 8px",
                            border: "1px solid #d1d5db",
                            borderRadius: "5px",
                            fontSize: "14px",
                            boxSizing: "border-box",
                          }}
                        />
                        <button
                          onClick={() => supprimerMeuble(index)}
                          style={{
                            height: "35px",
                            width: "35px",
                            flexShrink: 0,
                            border: "1px solid #fca5a5",
                            borderRadius: "5px",
                            background: "white",
                            color: "#ef4444",
                            cursor: "pointer",
                            fontSize: "16px",
                          }}
                        >
                          ✕
                        </button>
                      </div>
                    ))}

                    <button
                      onClick={ajouterMeuble}
                      style={{
                        height: "32px",
                        padding: "0 12px",
                        border: "1px solid #d1d5db",
                        borderRadius: "5px",
                        background: "white",
                        fontSize: "13px",
                        color: "#374151",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                      }}
                    >
                      <span style={{ fontSize: "16px" }}>+</span> Ajouter un
                      meuble
                    </button>
                  </div>
                </div>
              )}

              {/* Options libres */}
             

              <div className="grid">
                <div className="col-12">
                  {options.map((option, index) => (
                    <div
                      key={index}
                      style={{
                        display: "flex",
                        gap: "8px",
                        alignItems: "flex-end",
                        marginTop: "8px",
                      }}
                    >
                      <div style={{ flex: 2 }}>
                        <label
                          style={{
                            fontSize: "11px",
                            color: "#374151",
                            display: "block",
                            marginBottom: "2px",
                          }}
                        >
                          Titre
                        </label>
                        <input
                          value={option.titre}
                          onChange={(e) =>
                            modifierOption(index, "titre", e.target.value)
                          }
                          placeholder="Garde-meuble, stationnement..."
                          style={{
                            width: "100%",
                            height: "35px",
                            padding: "0 8px",
                            border: "1px solid #d1d5db",
                            borderRadius: "5px",
                            fontSize: "14px",
                          }}
                        />
                      </div>
                      <div style={{ flex: 2 }}>
                        <label
                          style={{
                            fontSize: "11px",
                            color: "#374151",
                            display: "block",
                            marginBottom: "2px",
                          }}
                        >
                          Description
                        </label>
                        <input
                          value={option.description}
                          onChange={(e) =>
                            modifierOption(index, "description", e.target.value)
                          }
                          placeholder="Description"
                          style={{
                            width: "100%",
                            height: "35px",
                            padding: "0 8px",
                            border: "1px solid #d1d5db",
                            borderRadius: "5px",
                            fontSize: "14px",
                          }}
                        />
                      </div>
                      <div style={{ width: "80px" }}>
                        <label
                          style={{
                            fontSize: "11px",
                            color: "#374151",
                            display: "block",
                            marginBottom: "2px",
                          }}
                        >
                          Prix €
                        </label>
                        <input
                          type="number"
                          value={option.prix}
                          onChange={(e) =>
                            modifierOption(
                              index,
                              "prix",
                              parseFloat(e.target.value) || 0,
                            )
                          }
                          placeholder="0"
                          min={0}
                          step={1}
                          style={{
                            width: "100%",
                            height: "35px",
                            padding: "0 8px",
                            border: "1px solid #d1d5db",
                            borderRadius: "5px",
                            fontSize: "14px",
                          }}
                        />
                      </div>
                      <div>
                        <button
                          onClick={() => supprimerOption(index)}
                          style={{
                            height: "35px",
                            width: "35px",
                            border: "1px solid #fca5a5",
                            borderRadius: "5px",
                            background: "white",
                            color: "#ef4444",
                            cursor: "pointer",
                            fontSize: "16px",
                          }}
                        >
                          ✕
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Récapitulatif du devis */}
              <div className="grid mt-2">
                <div className="col-12">
                  <div
                    style={{
                      background: "#f5f3eb",
                      borderRadius: "5px",
                      padding: "8px 12px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        padding: "4px 0",
                        fontSize: "14px",
                      }}
                    >
                      <span>
                        Logistique ({nbDemenageurs} déménageur(s), {nbCamions}{" "}
                        camion(s))
                      </span>
                      <span>{prixLogistique.toFixed(2)} €</span>
                    </div>

                    {nbCartons > 0 && (
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          padding: "4px 0",
                          fontSize: "14px",
                          color: "#6b7280",
                        }}
                      >
                        <span>Cartons ({nbCartons})</span>
                        <span></span>
                      </div>
                    )}

                    {miseEnCarton && (
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          padding: "4px 0",
                          fontSize: "14px",
                          color: "#6b7280",
                        }}
                      >
                        <span>Mise en carton</span>
                        <span></span>
                      </div>
                    )}

                    {demontage && (
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          padding: "4px 0",
                          fontSize: "14px",
                          color: "#6b7280",
                        }}
                      >
                        <span>
                          Démontage/remontage
                          {meublesDemontageList.filter((m) => m.trim() !== "")
                            .length > 0 &&
                            ` (${meublesDemontageList
                              .filter((m) => m.trim() !== "")
                              .join(", ")})`}
                        </span>
                        <span></span>
                      </div>
                    )}

                    {options.map((opt, idx) => (
                      <div
                        key={idx}
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          padding: "4px 0",
                          fontSize: "14px",
                        }}
                      >
                        <span>{opt.titre || "Option"}</span>
                        <span>{(opt.prix || 0).toFixed(2)} €</span>
                      </div>
                    ))}

                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        padding: "6px 0",
                        borderTop: "1px solid #d1d5db",
                        marginTop: "4px",
                        fontWeight: 600,
                        fontSize: "14px",
                      }}
                    >
                      <span>Sous-total</span>
                      <span>{sousTotal.toFixed(2)} €</span>
                    </div>

                    {remise > 0 && (
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          padding: "4px 0",
                          fontSize: "14px",
                          color: "#ef4444",
                        }}
                      >
                        <span>Remise ({remise}%)</span>
                        <span>- {montantRemise.toFixed(2)} €</span>
                      </div>
                    )}

                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        padding: "6px 0",
                        borderTop: "1px solid #d1d5db",
                        marginTop: "4px",
                        fontWeight: 700,
                        fontSize: "15px",
                      }}
                    >
                      <span>Total TTC</span>
                      <span>{total.toFixed(2)} €</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Le clic sur le bouton ouvre toujours le menu : pas d'envoi par défaut, le compte est obligatoire */}
              {/* <div className="grid mt-2">
                <div className="col-12">
                  <SplitMenuButton
                    variant="button"
                    label="Générer le devis"
                    loadingLabel="Génération..."
                    loading={generating}
                    icon="pi pi-file"
                    items={generateMenuItems}
                    disabled={generating || total <= 0}
                  />
                </div>
              </div> */}


<div className="grid mt-2">
  <div className="col-12">
    <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
      <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
        <select
          value={selectedGenerateMode}
          onChange={(e) => setSelectedGenerateMode(e.target.value)}
          style={{
            flex: 1,
            padding: "8px 12px",
            borderRadius: "6px",
            border: "1px solid #bfdbfe",
            background: "#dbeafe",
            color: "#1d4ed8",
            fontSize: "14px",
            fontWeight: 500,
            cursor: "pointer",
            outline: "none",
            height: "42px",
          }}
        >
          <option value="otedalon">Otedalon</option>
          <option value="zotdevis">ZotDevis</option>
        </select>

        <Button
          onClick={() => handleGenererDevis(selectedGenerateMode)}
          disabled={generating || total <= 0}
          style={{
            padding: "8px 20px",
            borderRadius: "6px",
            border: "none",
            background: "#2563eb",
            color: "white",
            fontSize: "14px",
            fontWeight: 500,
            cursor: generating || total <= 0 ? "not-allowed" : "pointer",
            opacity: generating || total <= 0 ? 0.6 : 1,
            height: "42px",
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <i className={generating ? "pi pi-spin pi-spinner" : "pi pi-file"} />
          {generating ? "Génération..." : "Générer"}
        </Button>
      </div>
    </div>
  </div>
</div>



            </>
          ) : (
            <>
              {/* Historique de tous les devis de ce client */}
              <div className="grid mt-3">
                <div className="col-12">
                  <span className="font-bold text-sm mb-2 block">
                    Devis de ce client
                  </span>
                  <DataTable
  value={devisList}
  loading={loadingList}
  size="small"
  emptyMessage="Aucun devis"
  className="text-sm"
>

  

  {/* Date de création */}
  <Column
    header="Date"
    style={{ width: "100px" }}
    body={(row) =>
      new Date(row.date_creation).toLocaleDateString("fr-FR")
    }
  />

  {/* N° devis */}
  <Column
    field="numero_devis"
    header="N° devis"
    style={{ width: "120px", textAlign: "center" }}
    body={(row) => (
      <span style={{ whiteSpace: "nowrap" }}>
        {row.numero_devis}
      </span>
    )}
  />

  {/* Date d'envoi */}
  <Column
    field="date_envoi"
    header="Date envoi"
    style={{ width: "100px" }}
    body={(row) =>
      row.date_envoi ? new Date(row.date_envoi).toLocaleDateString("fr-FR") : "-"
    }
  />

  {/* Compte */}
  <Column 
  field="mode" 
  header="Compte (R/O)"
  style={{ width: "130px" }}
  body={(row) => (
    <div style={{ 
      display: "flex", 
      alignItems: "center", 
      gap: "4px",
      whiteSpace: "nowrap"
    }}>
      <span>
        {row.mode}
      </span>
      
      {/* Checks type WhatsApp */}
      <span style={{ 
        display: "inline-flex",
        alignItems: "center",
        gap: "1px",
        marginLeft: "2px"
      }}>
        {/* 1er check - Email reçu */}
        <span style={{
          display: "inline-block",
          width: "12px",
          height: "12px",
          borderRadius: "50%",
          border: `2px solid ${row.recu == 1 ? '#22c55e' : '#d1d5db'}`,
          background: row.recu == 1 ? '#22c55e' : 'transparent',
          position: "relative"
        }}>
          {row.recu == 1 && (
            <span style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              color: "white",
              fontSize: "7px"
            }}>✓</span>
          )}
        </span>
        
        {/* 2ème check - Email ouvert */}
        <span style={{
          display: "inline-block",
          width: "12px",
          height: "12px",
          borderRadius: "50%",
          border: `2px solid ${row.ouvert == 1 ? '#22c55e' : '#d1d5db'}`,
          background: row.ouvert == 1 ? '#22c55e' : 'transparent',
          position: "relative",
          marginLeft: "-4px"
        }}>
          {row.ouvert == 1 && (
            <span style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              color: "white",
              fontSize: "7px"
            }}>✓</span>
          )}
        </span>
      </span>
    </div>
  )}
/>

  {/* Actions */}
  <Column
    header="Actions"
    style={{ width: "180px" }}
    body={(row) => (
      <div
        className="flex gap-1 align-items-center"
        style={{ flexWrap: "nowrap" }}
      >
        <Button
          icon="pi pi-eye"
          rounded
          text
          size="small"
          style={{
            color: "#1d4ed8",
            width: "28px",
            height: "28px",
            fontSize: "12px",
            background: "transparent",
            border: "none",
            boxShadow: "none",
            transition: "none",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "transparent";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "transparent";
          }}
          disabled={!row.lien_devis}
          onClick={() =>
            window.open(row.lien_devis, "_blank")
          }
        />
        <Button
          icon="pi pi-pencil"
          rounded
          text
          size="small"
          style={{
            color: "#c2410c",
            width: "28px",
            height: "28px",
            fontSize: "12px",
            border: "none",
            boxShadow: "none",
            transition: "none",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "transparent";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "transparent";
          }}
          disabled={!row.idVosFactures}
          onClick={() =>
            window.open(
              `${
                VOSFACTURES_BASE_URL[row.mode_envoi] ||
                VOSFACTURES_BASE_URL.otedalon
              }/invoices/${row.idVosFactures}/edit`,
              "_blank",
            )
          }
        />
        <Button
          icon="pi pi-download"
          rounded
          text
          size="small"
          style={{
            color: "#dc2626",
            width: "28px",
            height: "28px",
            fontSize: "12px",
            border: "none",
            boxShadow: "none",
            transition: "none",
            marginRight: "6px",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "transparent";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "transparent";
          }}
          disabled={!row.lien_pdf}
          onClick={() => window.open(row.lien_pdf, "_blank")}
        />

        <SplitMenuButton
          variant="icon"
          icon="pi pi-envelope"
          iconColor="#47dc26b9"
          items={getResendItems(row)}
        />
      </div>
    )}
  />
</DataTable>
                </div>
              </div>
            </>
          )}
        </div>
      </Dialog>
    </>
  );
}